import React from 'react';
import './Countdown.scss';
import dropWhile from 'lodash/dropWhile';
import { PreciseDuration } from '../../utils/date';
import cls from 'classnames';

export enum CountdownDisplayType {
  // eslint-disable-next-line no-unused-vars
  Thin = 'thin',
  // eslint-disable-next-line no-unused-vars
  Thick = 'thick',
}

export function Countdown({
  countdown,
  concise,
  displayMilliseconds,
  displayType = CountdownDisplayType.Thick,
}: CountdownProps) {
  const shouldRender = (amount: number) => !concise || amount !== 0;

  const { weeks = 0, days = 0, hours = 0, minutes = 0, seconds = 0, milliseconds = 0 } = countdown;
  const cumulativeDays = days + weeks * 7;

  const parts: Array<Omit<CountdownSectionProps, 'displayType'>> = [
    {
      label: 'd',
      time: cumulativeDays,
      padStart: 2,
    },
    {
      label: 'h',
      time: hours,
      padStart: 2,
    },
    {
      label: 'm',
      time: minutes,
      padStart: 2,
    },
    {
      label: 's',
      time: seconds,
      padStart: 2,
    },
  ];

  // don't want to show milliseconds if it's not relevant
  if (displayMilliseconds && milliseconds !== 0) {
    parts.push({
      label: 'ms',
      time: milliseconds,
      padStart: 3,
    });
  }

  // We only want to drop of the leading parts of the countdown when not relevant
  // without messing with the order
  const filteredParts = dropWhile(parts, (part) => !shouldRender(part.time));

  return (
    <div className="countdown-wrapper">
      {filteredParts.map((part) => (
        <CountdownSection
          time={part.time}
          label={part.label}
          key={part.label}
          displayType={displayType}
          padStart={part.padStart}
        />
      ))}
    </div>
  );
}

export type CountdownProps = {
  countdown: PreciseDuration;
  /**
   * Hide sections of the countdown that are 0
   */
  concise?: boolean;
  displayMilliseconds?: boolean;
  displayType?: CountdownDisplayType;
};

type CountdownSectionProps = {
  time: number;
  label: string;
  padStart?: number;
  displayType: CountdownDisplayType;
};

function CountdownSection({ time, label, displayType, padStart }: CountdownSectionProps) {
  const timeString = time.toString();
  const stringRepresentation =
    padStart === undefined ? timeString : timeString.padStart(padStart, '0');
  return (
    <div className="countdown-section">
      <span className={cls('countdown-number', displayType)}>{stringRepresentation}</span>
      <span className="countdown-label">{label}</span>
    </div>
  );
}
